import { useEffect, useState } from 'react';
import { adminService, type FarmOption, type CreateAnimalRequest } from '../../services/admin.service';
import { httpClient } from '../../services/http-client';
import { API_ENDPOINTS } from '../../config/api';
import './AdminAddAnimal.css';

interface AnimalTypeOption {
  id: number;
  code: string;
  nameRu: string;
  nameKz: string;
  nameEn: string;
}

interface BreedOption {
  id: number;
  nameRu: string;
  animalTypeId: number;
}

interface StatusOption {
  id: number;
  nameRu: string;
  code: string;
}

export default function AdminAddAnimal() {
  const [farms, setFarms] = useState<FarmOption[]>([]);
  const [animalTypes, setAnimalTypes] = useState<AnimalTypeOption[]>([]);
  const [breeds, setBreeds] = useState<BreedOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [form, setForm] = useState<CreateAnimalRequest>({
    animalTypeId: null,
    farmId: null,
    identifierNumber: '',
    registrationNumber: '',
    name: '',
    chipNumber: '',
    birthDate: '',
    breedId: null,
    birthPlace: '',
    birthWeight: null,
    weaningDate: '',
    weaningWeight: null,
    weight12m: null,
    isBreeding: null,
    hasHorns: false,
    sex: null,
    statusId: null,
    breedPurity: null,
  });

  useEffect(() => {
    Promise.all([
      adminService.getFarms(),
      httpClient.get<AnimalTypeOption[]>(API_ENDPOINTS.ANIMAL_TYPE),
      httpClient.get<BreedOption[]>(API_ENDPOINTS.BREED),
      httpClient.get<StatusOption[]>(API_ENDPOINTS.ANIMAL_STATUS),
    ])
      .then(([fRes, atRes, bRes, sRes]) => {
        if (fRes.success && fRes.data) setFarms(fRes.data);
        if (atRes.success && atRes.data) setAnimalTypes(atRes.data);
        if (bRes.success && bRes.data) setBreeds(bRes.data);
        if (sRes.success && sRes.data) setStatuses(sRes.data);
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка загрузки справочников' }))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field: keyof CreateAnimalRequest, value: string | number | boolean | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.identifierNumber.trim()) {
      setMessage({ type: 'err', text: 'Укажите идентификационный номер' });
      return;
    }
    setSubmitLoading(true);
    setMessage(null);
    const payload: CreateAnimalRequest = {
      ...form,
      farmId: form.farmId ? Number(form.farmId) : null,
      animalTypeId: form.animalTypeId ? Number(form.animalTypeId) : null,
      breedId: form.breedId ? Number(form.breedId) : null,
      statusId: form.statusId ? Number(form.statusId) : null,
      sex: form.sex ? Number(form.sex) : null,
      birthWeight: form.birthWeight != null ? Number(form.birthWeight) : null,
      weaningWeight: form.weaningWeight != null ? Number(form.weaningWeight) : null,
      weight12m: form.weight12m != null ? Number(form.weight12m) : null,
      breedPurity: form.breedPurity != null ? Number(form.breedPurity) : null,
    };
    adminService
      .createAnimal(payload)
      .then((res) => {
        if (res.success) {
          setMessage({ type: 'ok', text: 'Животное создано' });
          setForm({
            animalTypeId: null,
            farmId: null,
            identifierNumber: '',
            registrationNumber: '',
            name: '',
            chipNumber: '',
            birthDate: '',
            breedId: null,
            birthPlace: '',
            birthWeight: null,
            weaningDate: '',
            weaningWeight: null,
            weight12m: null,
            isBreeding: null,
            hasHorns: false,
            sex: null,
            statusId: null,
            breedPurity: null,
          });
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка создания' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка создания животного' }))
      .finally(() => setSubmitLoading(false));
  };

  if (loading) {
    return (
      <div className="admin-add-animal">
        <h1>Добавить животное</h1>
        <p className="admin-add-animal-loading">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="admin-add-animal">
      <h1>Добавить животное</h1>
      <p className="admin-add-animal-desc">Создание записи о животном (любая ферма)</p>

      <form className="admin-add-animal-form" onSubmit={handleSubmit}>
        <div className="admin-add-animal-row">
          <label>Ферма *</label>
          <select
            value={form.farmId ?? ''}
            onChange={(e) => handleChange('farmId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Выберите ферму —</option>
            {farms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} (ID: {f.id})
              </option>
            ))}
          </select>
        </div>

        <div className="admin-add-animal-row">
          <label>Идентификационный номер *</label>
          <input
            type="text"
            value={form.identifierNumber}
            onChange={(e) => handleChange('identifierNumber', e.target.value)}
            placeholder="Обязательное поле"
            maxLength={100}
          />
        </div>

        <div className="admin-add-animal-row">
          <label>Регистрационный номер</label>
          <input
            type="text"
            value={form.registrationNumber ?? ''}
            onChange={(e) => handleChange('registrationNumber', e.target.value || null)}
            maxLength={100}
          />
        </div>

        <div className="admin-add-animal-row">
          <label>Кличка</label>
          <input
            type="text"
            value={form.name ?? ''}
            onChange={(e) => handleChange('name', e.target.value || null)}
            maxLength={200}
          />
        </div>

        <div className="admin-add-animal-row">
          <label>Тип животного</label>
          <select
            value={form.animalTypeId ?? ''}
            onChange={(e) =>
              handleChange('animalTypeId', e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">— Не выбрано —</option>
            {animalTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nameRu}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-add-animal-row">
          <label>Порода</label>
          <select
            value={form.breedId ?? ''}
            onChange={(e) => handleChange('breedId', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Не выбрано —</option>
            {breeds.map((b) => (
              <option key={b.id} value={b.id}>
                {b.nameRu}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-add-animal-row">
          <label>Статус</label>
          <select
            value={form.statusId ?? ''}
            onChange={(e) =>
              handleChange('statusId', e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">— Не выбрано —</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nameRu}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-add-animal-row">
          <label>Пол</label>
          <select
            value={form.sex ?? ''}
            onChange={(e) => handleChange('sex', e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Не выбрано —</option>
            <option value={1}>Самец</option>
            <option value={2}>Самка</option>
          </select>
        </div>

        <div className="admin-add-animal-row">
          <label>Чип номер</label>
          <input
            type="text"
            value={form.chipNumber ?? ''}
            onChange={(e) => handleChange('chipNumber', e.target.value || null)}
            maxLength={100}
          />
        </div>

        <div className="admin-add-animal-row">
          <label>Дата рождения</label>
          <input
            type="date"
            value={form.birthDate ?? ''}
            onChange={(e) => handleChange('birthDate', e.target.value || null)}
          />
        </div>

        <div className="admin-add-animal-row">
          <label>Рога</label>
          <select
            value={form.hasHorns ? '1' : '0'}
            onChange={(e) => handleChange('hasHorns', e.target.value === '1')}
          >
            <option value="0">Нет</option>
            <option value="1">Да</option>
          </select>
        </div>

        {message && (
          <p
            className={
              message.type === 'ok' ? 'admin-add-animal-msg-ok' : 'admin-add-animal-msg-err'
            }
          >
            {message.text}
          </p>
        )}

        <div className="admin-add-animal-actions">
          <button type="submit" disabled={submitLoading}>
            {submitLoading ? 'Создание...' : 'Создать животное'}
          </button>
        </div>
      </form>
    </div>
  );
}
